import type { NodeRenderer } from "@tento-ui/brand/page-engine.types";
import type {
  JsonValue,
  PageModel,
  PageNode,
} from "@tento-ui/brand/page-model";

import { BlogIndexController } from "./blog-index-controller";
import type { BlogPost } from "../blog/blog-data";
import type { BlogVariantConfig } from "../blog/blog-variant";
import { DomUtils } from "@tento-ui/dom";
import type { Brand } from "@tento-ui/brand/brand";

export type PageEngineContext = {
  blog: Option<BlogVariantConfig>;
  landing: Option<Brand.LandingPage["landing"]["sections"]>;
  onPostNavigate: (slug: string) => void;
  onBlogBack: () => void;
};

export type PageEngineNodeProps = {
  page: PageModel;
  node: PageNode;
  context: PageEngineContext;
};

const postListControllers = new WeakMap<HTMLElement, BlogIndexController>();

export function toProps(
  page: PageModel,
  node: PageNode,
  context: PageEngineContext,
): PageEngineNodeProps {
  return { page, node, context };
}

export const postListRenderer: NodeRenderer<PageEngineNodeProps> = {
  make(props: PageEngineNodeProps): HTMLElement {
    if (!props.context.blog) return document.createElement("div");

    const controller = new BlogIndexController(
      props.context.blog,
      props.context.onPostNavigate,
      BlogIndexController.stateFromNode(props.node),
    );

    const $element = controller.element;
    postListControllers.set($element, controller);
    return $element;
  },
  bind(
    $element: HTMLElement,
    props: PageEngineNodeProps,
  ): HTMLElement {
    const controller = postListControllers.get($element);
    if (!controller) return hydratePostListPlaceholder($element, props);

    controller.hydrateState(BlogIndexController.stateFromNode(props.node));
    return $element;
  },
  skeleton: null,
};

export const postBodyRenderer: NodeRenderer<PageEngineNodeProps> = {
  make(props: PageEngineNodeProps): HTMLElement {
    return makePostBodyPagelet(props);
  },
  bind(
    $element: HTMLElement,
    props: PageEngineNodeProps,
  ): HTMLElement {
    DomUtils.clearChildren($element, false);
    $element.appendChild(makePostBodyPagelet(props));
    $element.dataset.pageHydrated = "true";
    return $element;
  },
  skeleton: null,
};

function hydratePostListPlaceholder(
  $element: HTMLElement,
  props: PageEngineNodeProps,
): HTMLElement {
  if (!props.context.blog) return $element;

  const controller = new BlogIndexController(
    props.context.blog,
    props.context.onPostNavigate,
    BlogIndexController.stateFromNode(props.node),
  );

  DomUtils.clearChildren($element, false);
  $element.appendChild(controller.element);
  $element.dataset.pageHydrated = "true";
  postListControllers.set($element, controller);
  return $element;
}

function postFromJson(value: JsonValue): Option<BlogPost> {
  const object = asObject(value);
  if (!object) return null;

  return {
    slug: getString(object, "slug"),
    title: getString(object, "title"),
    date: getString(object, "date"),
    summary: getString(object, "summary"),
    heroImage: getString(object, "heroImage"),
    authorName: getString(object, "authorName"),
    authorRole: getOptionString(object, "authorRole"),
    category: getString(object, "category"),
    featured: getBoolean(object, "featured"),
    tags: getStringArray(object, "tags"),
    content: getOptionString(object, "content"),
    contentHtml: getOptionString(object, "contentHtml"),
  };
}

function makePostBodyPagelet(props: PageEngineNodeProps): HTMLElement {
  if (!props.context.blog) {
    return document.createElement("div");
  }

  const post = postFromJson(props.node.props.post);
  if (!post) {
    return document.createElement("div");
  }

  const delegate = { onBack: props.context.onBlogBack };
  const $pagelet = props.context.blog.routes.post.make(post, delegate);

  const $title = props.context.blog.blogContent.title.make(post);
  $title.slot = "title";
  const $meta = props.context.blog.blogContent.meta.make(post);
  $meta.slot = "meta";
  $pagelet.append($title, $meta);

  return $pagelet;
}

function getArray(object: PageNode["props"], key: string): JsonValue[] {
  const value = object[key];
  return Array.isArray(value) ? value : [];
}

function asObject(value: JsonValue): Option<PageNode["props"]> {
  if (value === null) return null;
  if (Array.isArray(value)) return null;
  if (typeof value !== "object") return null;
  return value;
}

function getString(object: PageNode["props"], key: string): string {
  const value = object[key];
  return typeof value === "string" ? value : "";
}

function getOptionString(object: PageNode["props"], key: string): Option<string> {
  const value = object[key];
  return typeof value === "string" ? value : null;
}

function getBoolean(object: PageNode["props"], key: string): boolean {
  const value = object[key];
  return typeof value === "boolean" ? value : false;
}

function getStringArray(object: PageNode["props"], key: string): string[] {
  const strings: string[] = [];
  for (const value of getArray(object, key)) {
    if (typeof value === "string") strings.push(value);
  }
  return strings;
}
